// Thin wrapper around Capacitor native APIs.
// All exports are safe to call on web — they no-op when not running natively.
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';

// ── Push notifications ───────────────────────────────────────────────────────
// Returns the FCM/APNs token string, or null if unavailable / denied.
export async function registerNativePush() {
  if (!isNative) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return null;
    await PushNotifications.register();
    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => resolve(token.value));
      PushNotifications.addListener('registrationError', () => resolve(null));
      // Timeout after 10s
      setTimeout(() => resolve(null), 10000);
    });
  } catch {
    return null;
  }
}

// Fires handler(url) whenever the app is opened from a Universal Link / custom scheme.
// Returns a cleanup function.
export function addDeepLinkListener(handler) {
  if (!isNative) return () => {};
  let cleanup = () => {};
  import('@capacitor/app').then(({ App: CapApp }) => {
    CapApp.addListener('appUrlOpen', (event) => handler(event.url));
    cleanup = () => CapApp.removeAllListeners();
  });
  return () => cleanup();
}

// ── Status bar ───────────────────────────────────────────────────────────────
export async function setupStatusBar() {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Light });
  } catch { /* not critical */ }
}

// ── Splash screen ────────────────────────────────────────────────────────────
export async function hideSplash() {
  if (!isNative) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch { /* not critical */ }
}
